import numeral from 'numeral';
import { useTranslation } from 'react-i18next';
import { useContext, memo, useState } from 'react';
import useEvents from 'lib/hooks/useEvents';
import tapEvent from 'lib/Tracking/Vdp/VehicleSnapshot/TapEvent';
import VdpEventContext from 'contexts/VdpEventContext';
import formatRawPhoneNumber from 'lib/formatRawPhoneNumber';
import { DealerPage as DealerPageModel } from 'lib/models/DealerPage';
import MonthlyPaymentCta from 'components/Srp/MonthlyPaymentCalculator/MonthlyPaymentCta';
import Modal from 'react-modal';
import MonthlyPaymentCalculator from 'components/Srp/MonthlyPaymentCalculator';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import DealerAvailability from './DealerHours';
import styles from './styles';

interface SmsVariantVehicleSnapshotProps {
  make: string;
  model: string;
  year: number;
  trim: string | null;
  price: number;
  monthlyPayment: number;
  mileage: number | null;
  location: string;
  phoneNumber: string | null;
  openLeadForm: () => void;
  zip: string;
  dealerPage?: DealerPageModel;
}

function SmsVariantVehicleSnapshot({
  make,
  model,
  year,
  trim,
  price,
  monthlyPayment,
  mileage,
  location,
  openLeadForm,
  phoneNumber,
  dealerPage,
}: SmsVariantVehicleSnapshotProps): JSX.Element {
  const { t } = useTranslation();
  const title = `${year} ${make} ${model} ${trim}`;
  const logger = useEvents();
  const { trackingParams } = useContext(VdpEventContext);

  function logVehicleSnapshotTap(action: string): void {
    logger.log(tapEvent({ action, trackingParams }));
  }

  let formattedPhoneNumber: string | undefined;
  if (phoneNumber) {
    formattedPhoneNumber = formatRawPhoneNumber(phoneNumber);
  }

  const [showMonthlyPaymentModal, setShowMonthlyPaymentModal] = useState(false);
  function handleCloseMonthlyPaymentDialog(): void {
    setShowMonthlyPaymentModal(false);
  }
  function onMonthlyPaymentCalculatorClick(): void {
    setShowMonthlyPaymentModal(true);
  }

  const { isMobile } = useDeviceScreen();
  const monthlyPaymentModalStyles: ReactModal.Styles = {
    content: {
      height: isMobile ? '100%' : '600px',
      width: isMobile ? '100%' : '456px',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      overflow: 'hidden',
      padding: '45px 35px 35px 35px',
    },
    overlay: {
      zIndex: 999,
      overflow: 'hidden',
      backgroundColor: 'rgba(0,0,0,0.4)',
    },
  };

  return (
    <div className="vehicle-snapshot">
      <div className="title-container">
        <div className="title">{title}</div>
        <div className="price-container">
          <div className="price">{numeral(price).format('$0,0')}</div>
          <MonthlyPaymentCta value={monthlyPayment} onClick={onMonthlyPaymentCalculatorClick} />
        </div>
      </div>
      <div className="secondary-info">
        <div className="mileage-mobile">{`${numeral(mileage).format('0a')} ${t('vdp.miles')}`}</div>
        <div className="separator">|</div>
        <div className="mileage-desktop">{`${numeral(mileage).format('0,0')} ${t(
          'vdp.miles'
        )}`}</div>
        <div className="location">{location}</div>
      </div>
      <div className="request-info-container">
        <div className="request-info-title">{t('vdp.requestInfo')}</div>
        <div className="dealer-hours-container">
          <DealerAvailability dealerHours={dealerPage?.dealer.hours} />
        </div>
        <div className="sms-button-container">
          <button className="sms-button">
            <div className="sms-icon" />
            <div className="sms">{t('vdp.actions.text')}</div>
          </button>
        </div>
        <div className="contact-button-container">
          {formattedPhoneNumber && phoneNumber && (
            <button className="call-button" onClick={(): void => logVehicleSnapshotTap('call')}>
              <a href={`tel: ${phoneNumber}`} target="_parent" className="call-link">
                <div className="call-icon" />
                <div className="call">{t('vdp.actions.call')}</div>
              </a>
            </button>
          )}
          <button
            className="email-button"
            onClick={(): void => {
              logVehicleSnapshotTap('email');
              openLeadForm();
            }}
          >
            <div className="email-icon" />
            <div className="email">{t('vdp.actions.email')}</div>
          </button>
        </div>
      </div>
      <Modal
        isOpen={showMonthlyPaymentModal}
        onRequestClose={handleCloseMonthlyPaymentDialog}
        shouldCloseOnOverlayClick={true}
        style={monthlyPaymentModalStyles}
      >
        <MonthlyPaymentCalculator onClick={handleCloseMonthlyPaymentDialog} />
      </Modal>
      <style jsx>{styles}</style>
    </div>
  );
}

function arePropsEqual(
  prevProps: SmsVariantVehicleSnapshotProps,
  nextProps: SmsVariantVehicleSnapshotProps
): boolean {
  const keys = [
    'make',
    'model',
    'year',
    'trim',
    'price',
    'monthlyPayment',
    'mileage',
    'location',
    'phoneNumber',
    'openLeadForm',
  ] as const;

  return keys.every((key) => prevProps[key] === nextProps[key]);
}

export default memo(SmsVariantVehicleSnapshot, arePropsEqual);
